import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, DropdownButton, MenuItem } from 'react-bootstrap';
import Toggle from 'react-toggle';
import type { VoidFunctionComponent } from 'react';
import { Action, ResourceType } from '../../../../../typings/permissions';
import { orgRolesList, roleLabel } from '../../../../helpers/role';
import RolesModules from '../../../../redux/roles';
import UsersModule from '../../../../redux/users';
import Button, { ButtonGroup } from '../../../generic/Button';
import Flex from '../../../generic/Flex';
import Loader from '../../../generic/Loader';
import Text from '../../../generic/Text';
import Tooltip from '../../../generic/Tooltip';
import { FormGroup, FormLabel, LabeledTextInput } from '../../../generic/Forms';
import TeamRoleSelector from '../teams/TeamRoleSelector';
import { userHasPermissionToEditTeamUsers } from '../../../../helpers/permissions';
import { hasError } from '../../../../utils';
import { window } from '../../../../globals';
import { useOnMount } from '../../../../hooks';
import { useParams, useRouter } from '../../../../contexts/RouterContext';
import type { ReduxStore } from '../../../../redux/store';
import type { SetUserTeamRolesRequest } from '../../../../../typings/api';

const EditUserSettings: VoidFunctionComponent = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { id: userId } = useParams<{ id: number }>();
  const user = useSelector((store: ReduxStore) => UsersModule.selectors.getUser(store, userId));
  const status = useSelector(UsersModule.selectors.getStatus);
  const roles = useSelector(RolesModules.selectors.getRoles);

  const [selectedRoleId, setSelectedRoleId] = useState(user?.userRole?.roleId || null);
  const [isEnabled, setIsEnabled] = useState(user?.enabled ?? true);
  const [userTeamRoles, setUserTeamRoles] = useState(user?.teamUsers || []);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const updateRole = (roleId: number) =>
    dispatch(UsersModule.actions.updatePermission(userId, roleId));

  const updateTeamRoles = (newRoles: SetUserTeamRolesRequest) =>
    dispatch(UsersModule.actions.setTeamRoles(userId, newRoles));

  const updateEnabled = (enabled: boolean) =>
    dispatch(UsersModule.actions.updateEnabled(userId, enabled));

  useOnMount(() => {
    //  This will already be fetched if they navigated from the users list page,
    //  but if they refresh the page we need to ensure that the user is in the Redux store.
    dispatch(UsersModule.actions.fetchOne(userId));
    //  Ensures that the roles dropdown is populated.
    dispatch(RolesModules.actions.fetchAll());
  });

  useEffect(() => {
    setSelectedRoleId(user?.userRole?.roleId || null);
    setUserTeamRoles(user?.teamUsers || []);
    setIsEnabled(user?.enabled ?? true);
  }, [user]);

  if (status.loading || !user) {
    return (
      <div style={{ minHeight: '400px' }}>
        <Flex>
          <Loader />
        </Flex>
      </div>
    );
  }

  const canManageRoles = window.hasPermission({ resourceType: ResourceType.Role, action: Action.Manage });
  const canEditOrgRole = canManageRoles && window.user?.id !== Number(userId); // userId comes from params, which stringifies them.

  const submitUserUpdate = async () => {
    setFeedbackMessage(null);

    const promises: Array<Promise<void>> = [];

    if (canEditOrgRole && selectedRoleId !== (user.userRole?.roleId || null)) {
      promises.push(updateRole(selectedRoleId));
    }

    const teamRoleChanges = [];
    userTeamRoles.forEach(teamUser => {
      if (userHasPermissionToEditTeamUsers(teamUser.team.id)) {
        teamRoleChanges.push({ teamId: teamUser.team.id, roleId: teamUser.roleId });
      }
    });

    if (teamRoleChanges.length) {
      promises.push(updateTeamRoles(teamRoleChanges));
    }

    try {
      await Promise.all(promises);

      // Update the enabled status separately so that we don't clobber the "errors" array in Redux.
      if (isEnabled !== user.enabled) {
        await updateEnabled(isEnabled);
      }
    } catch (e) {
      // Errors are handled by Redux.
    }

    setHasUnsavedChanges(false);
    setFeedbackMessage('User settings successfully updated');
  };

  const cancelUserUpdate = () => {
    router.push('/account/settings/organization/users');
  };

  const updateUserTeamRoles = (index: number, roleId: number) => {
    // we create a copy of our teamUsers array
    const newRoles = [...userTeamRoles];

    // work on the matching team selected in the drop down
    const currTeam = newRoles[index];
    currTeam.roleId = roleId;

    setUserTeamRoles(newRoles); // for ux purposes, we update teamUsers with the newRoles array
  };

  const successAlert = () => {
    if (feedbackMessage) {
      return (
        <Alert
          bsStyle="success"
          dismissAfter={2000}
          onDismiss={() => {
            setFeedbackMessage(null);
          }}>
          {feedbackMessage}
        </Alert>
      );
    }

    return null;
  };

  return (
    <div className="padding-xl">
      {/* We don't display an error alert here because AccountSettingsPage does that. */}
      {!hasError(status) && successAlert()}
      <div className="margin-bottom-md">
        <Text.H1>
          {user.username}
        </Text.H1>
      </div>
      <Text.H2>
        User Details
      </Text.H2>
      <div className="width-half">
        <FormGroup spacing="lg">
          <LabeledTextInput
            label="Username"
            value={user.username || ''}
            placeholder="Username"
            isDisabled
            isRequired
          />
          <LabeledTextInput
            label="User Email"
            value={user.email || ''}
            placeholder="User Email"
            isDisabled
            isRequired
          />
        </FormGroup>
      </div>
      <div className="user-settings__status-toggle">
        <Tooltip showTooltip={!canEditOrgRole} tooltip="You are not permitted to enable or disable this user.">
          <div style={{ display: 'flex' }}>
            <Toggle
              id="user-settings-status-toggle"
              checked={isEnabled}
              onChange={(e) => {
                setIsEnabled(e.target.checked);
                setHasUnsavedChanges(true);
              }}
              icons={false}
              disabled={!canEditOrgRole} />
          </div>
        </Tooltip>
        <span className="padding-left-sm">
          <FormLabel
            label={isEnabled ? 'Enabled' : 'Disabled'}
            htmlFor="user-settings-status-toggle"
            isInline
          />
        </span>
      </div>
      <hr />
      <div className="width-half">
        <FormGroup spacing="lg">
          <>
            <Text.H2>
              Role
            </Text.H2>
            <FormLabel
              label="Inside the organization"
              htmlFor="user-org-role-dropdown"
              requiredText="(not required)"
            />
            <DropdownButton
              id="user-org-role-dropdown"
              title={roleLabel(roles, selectedRoleId)}
              onSelect={(roleId: number | null) => {
                setSelectedRoleId(roleId);
                setHasUnsavedChanges(true);
              }}
              bsStyle="default"
              disabled={!canEditOrgRole}>
              {orgRolesList(roles).map(role => (
                <MenuItem
                  key={role.id || 0}
                  eventKey={role.id}
                  active={selectedRoleId === role.id}
                  name="roleId">
                  {role.name}
                </MenuItem>
              ))}
            </DropdownButton>
          </>
          <>
            <Text.H2>
              Team
            </Text.H2>
            <Text.Paragraph>
              This user&apos;s roles in their teams.
            </Text.Paragraph>
            <FormGroup spacing="lg">
              {userTeamRoles.map((team, index) => (
                <Flex
                  key={team.team.id}
                  justify="flex-start"
                >
                  <FormLabel
                    htmlFor={`role-select-${team.team.id}`}
                    isInline
                    label={(
                      <span className="user-settings__team-name">
                        {team.team.name}
                      </span>
                    )}
                  />
                  <TeamRoleSelector
                    roles={roles}
                    userId={user.id}
                    roleId={team.roleId}
                    teamId={team.team.id}
                    onSetUserRole={roleId => {
                      updateUserTeamRoles(index, roleId);
                      setHasUnsavedChanges(true);
                    }}
                    disabled={
                      (window.user?.id === user.id && !canManageRoles) ||
                      !userHasPermissionToEditTeamUsers(team.team.id)
                    }
                  />
                </Flex>
              ))}
            </FormGroup>
          </>
        </FormGroup>
      </div>
      <div className="user-settings__action-buttons">
        <ButtonGroup>
          <Button
            type="secondary"
            size="medium"
            onClick={cancelUserUpdate}
          >
            Cancel
          </Button>
          <Button
            size="medium"
            onClick={submitUserUpdate}
            isDisabled={!hasUnsavedChanges}
          >
            Update
          </Button>
        </ButtonGroup>
      </div>
    </div>
  );
};

export default EditUserSettings;
